/**
 * Utility per la gestione degli utenti anonimi nel sistema di chat.
 * Gestisce la creazione, recupero e pulizia degli ID anonimi persistenti.
 * 
 * GDPR Compliant: Usa safeLocalStorage per rispettare il consenso utente.
 */

import { safeLocalStorage } from '@/utils/safeStorage';

const ANONYMOUS_USER_KEY = 'gildy_anonymous_user_id';

/**
 * Ottiene o crea un ID utente anonimo persistente.
 * L'ID viene salvato in localStorage per mantenere la continuità della sessione.
 * 
 * NOTA: Richiede consenso "preferences" per salvare l'ID.
 * Se il consenso non è dato, genera un ID temporaneo non persistente.
 * 
 * @returns {string} ID utente anonimo nel formato 'anonymous_{timestamp}_{randomString}'
 */
export const getOrCreateAnonymousId = (): string => {
  // Controlla se esiste già un ID in localStorage
  const existingId = safeLocalStorage.getItem(ANONYMOUS_USER_KEY);
  if (existingId) {
    return existingId;
  }
  
  // Genera nuovo ID anonimo con timestamp e stringa casuale
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const anonymousId = `anonymous_${timestamp}_${randomString}`;
  
  // Tenta di salvare in localStorage (rispetta il consenso GDPR)
  safeLocalStorage.setItem(ANONYMOUS_USER_KEY, anonymousId);
  
  return anonymousId;
};

/**
 * Verifica se un userId è di un utente anonimo.
 * 
 * @param {string} userId - ID utente da verificare
 * @returns {boolean} True se l'utente è anonimo
 */
export const isAnonymousUser = (userId: string): boolean => {
  return userId.startsWith('anonymous_');
};

/**
 * Rimuove l'ID anonimo dal localStorage.
 * Utilizzato dopo la migrazione delle conversazioni quando un utente si registra.
 */
export const clearAnonymousId = (): void => {
  safeLocalStorage.removeItem(ANONYMOUS_USER_KEY);
};

/**
 * Ottiene l'ID anonimo esistente senza crearne uno nuovo.
 * 
 * @returns {string | null} ID anonimo esistente o null se non presente
 */
export const getExistingAnonymousId = (): string | null => {
  return safeLocalStorage.getItem(ANONYMOUS_USER_KEY);
};

/**
 * Verifica se esiste un ID anonimo salvato.
 * 
 * @returns {boolean} True se esiste un ID anonimo
 */
export const hasAnonymousId = (): boolean => {
  return safeLocalStorage.getItem(ANONYMOUS_USER_KEY) !== null;
};
